<script setup lang="ts">
import type { IVideoClippedListItem } from "~/videos/helpers/get-angles-value";
import { formatTime } from "@/videos/helpers/format-time";

const props = defineProps<{
  item: IVideoClippedListItem;
  activeItem: IVideoClippedListItem | null;
  isVideoPaused?: boolean;
}>();

const emit = defineEmits<{
  itemClick: [item: IVideoClippedListItem];
}>();

const baseURL = useRuntimeConfig().app.baseURL;

const isActive = computed(() => props.item.id === props.activeItem?.id);

const thumbnailStateIcon = computed(() => {
  if (isActive.value && props.isVideoPaused) {
    return "play";
  }
  if (isActive.value) {
    return "pause-nocircle";
  }
  return "play";
});
</script>

<template>
  <div
    class="group mb-1 flex h-[60px] cursor-pointer items-center justify-between hover:bg-neutral-light-50 hover:dark:bg-neutral-dark-600 ltr:pr-4 rtl:pl-4"
    :class="{ 'bg-neutral-light-50 dark:bg-neutral-dark-500': isActive }"
    @click="emit('itemClick', item)"
  >
    <div class="flex basis-4/6 items-center pl-4">
      <div class="flex items-center gap-4">
        <div class="relative flex">
          <div
            class="absolute z-[7] flex size-full items-center justify-center rounded-sm group-hover:bg-black/45"
            :class="{ 'bg-black/45': isActive }"
          >
            <RIcon
              class="cursor-pointer text-sm text-white opacity-0 group-hover:opacity-100"
              :class="{ 'opacity-100': isActive }"
              :name="thumbnailStateIcon"
            />
          </div>
          <RImage
            class="relative h-10 w-[72px] shrink-0 rounded-sm object-cover"
            :src="item.poster || item.event.poster"
            :fallback-src="`${baseURL}images/no_poster.jpg`"
          />
        </div>
        <UClampedText
          class="max-w-28 flex-1 pr-1 text-sm font-medium text-black dark:text-white"
          :lines="2"
          :text="item.title"
        />
      </div>
    </div>

    <div class="flex basis-2/6 items-center justify-end gap-4">
      <IconPlaying
        v-if="isActive"
        :active="!isVideoPaused"
        class="inline-block pb-0.5 text-neutral-light-700"
      />
      <span
        v-if="item.video_duration != null"
        class="w-10 text-end text-sm font-medium dark:text-white"
      >
        {{ formatTime(item.video_duration) }}
      </span>

      <!-- Future per-source actions (playlist row uses UMenu here) -->
      <slot name="actions" />
    </div>
  </div>
</template>
